import React from 'react';
import {
  Paper, Typography, Box, Avatar, Badge, LinearProgress, Grid
} from '@mui/material';
import {
  Star, TrendingUp, Insights, EmojiNature
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

// Eco levels configuration matching backend
const ECO_LEVELS = [
  { name: "Rookie", minPoints: 0, maxPoints: 499, color: "#66bb6a", icon: "🌱" },
  { name: "Apprentice", minPoints: 500, maxPoints: 1999, color: "#43a047", icon: "⚔️" },
  { name: "Sorter", minPoints: 2000, maxPoints: 4999, color: "#2e7d32", icon: "♻️" },
  { name: "Pro Sorter", minPoints: 5000, maxPoints: 14999, color: "#1b5e20", icon: "🌍" },
  { name: "Master Sorter", minPoints: 15000, maxPoints: 49999, color: "#0d4717", icon: "👑" },
  { name: "Eco-Legend", minPoints: 50000, maxPoints: Infinity, color: "#004d40", icon: "🏆" }
];

const getEcoLevel = (points) => {
  return ECO_LEVELS.find(level => points >= level.minPoints && points <= level.maxPoints) || ECO_LEVELS[0];
};

const EcoStats = ({ user, history = [] }) => {
  const currentLevel = getEcoLevel(user?.rewardsPoints || 0);
  const nextLevel = ECO_LEVELS.find(level => level.minPoints > (user?.rewardsPoints || 0));
  const progressToNext = nextLevel ?
    ((user?.rewardsPoints || 0) - currentLevel.minPoints) / (nextLevel.minPoints - currentLevel.minPoints) * 100 : 100;

  // --- FIXED CALCULATION LOGIC ---

  // 1. Accuracy Rate - Based on average result_confidence from classifications
  const accuracyRate = history && history.length > 0 ?
    (history.reduce((sum, item) => sum + (item.resultConfidence || 0), 0) / history.length).toFixed(1) : '0.0';

  // 2. Days Streak - Based on classifiedAt dates
  let daysStreak = 0;
  if (history && history.length > 0) {
    const classificationDates = [...new Set(history.map(h => new Date(h.classifiedAt).toDateString()))]
      .map(dateStr => new Date(dateStr))
      .sort((a, b) => b.getTime() - a.getTime()); // Most recent first

    if (classificationDates.length > 0) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      yesterday.setHours(0, 0, 0, 0);

      const mostRecentDate = new Date(classificationDates[0]);
      mostRecentDate.setHours(0, 0, 0, 0);

      // Check if user classified today or yesterday to start streak
      if (mostRecentDate.getTime() === today.getTime() || mostRecentDate.getTime() === yesterday.getTime()) {
        daysStreak = 1;

        // Count consecutive days
        for (let i = 0; i < classificationDates.length - 1; i++) {
          const currentDate = new Date(classificationDates[i]);
          currentDate.setHours(0, 0, 0, 0);

          const nextDate = new Date(classificationDates[i + 1]);
          nextDate.setHours(0, 0, 0, 0);

          const diffTime = currentDate.getTime() - nextDate.getTime();
          const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

          if (diffDays === 1) {
            daysStreak++;
          } else {
            break; // Streak is broken
          }
        }
      }
    }
  }

  // 3. Top Category - Based on category.name from the API response
  let topCategory = 'None';
  if (history && history.length > 0) {
    const categoryStats = {};

    history.forEach(item => {
      if (item.category && item.category.name) {
        const categoryName = item.category.name;
        categoryStats[categoryName] = (categoryStats[categoryName] || 0) + 1;
      }
    });

    if (Object.keys(categoryStats).length > 0) {
      const maxCount = Math.max(...Object.values(categoryStats));
      const topCategories = Object.keys(categoryStats).filter(category => categoryStats[category] === maxCount);

      if (topCategories.length === 1) {
        topCategory = topCategories[0];
      } else {
        // Handle tie-breaker with most recent classification
        const sortedHistory = [...history].sort((a, b) => new Date(b.classifiedAt) - new Date(a.classifiedAt));
        for (const item of sortedHistory) {
          if (item.category && item.category.name && topCategories.includes(item.category.name)) {
            topCategory = item.category.name;
            break;
          }
        }
      }
    }
  }

  const totalClassifications = history?.length || 0;

  // Get level-specific styling
  const getLevelStyle = (level) => {
    const styles = {
      'Rookie': { bg: '#e8f5e8', color: '#2e7d32', border: '#4caf50' },
      'Apprentice': { bg: '#c8e6c9', color: '#1b5e20', border: '#43a047' },
      'Sorter': { bg: '#4caf50', color: '#ffffff', border: '#2e7d32' },
      'Pro Sorter': { bg: '#43a047', color: '#ffffff', border: '#1b5e20' },
      'Master Sorter': { bg: '#2e7d32', color: '#ffffff', border: '#1b5e20' },
      'Eco-Legend': { bg: '#1b5e20', color: '#ffffff', border: '#0d4717' },
    };
    return styles[level] || styles['Rookie'];
  };

  const levelStyle = getLevelStyle(currentLevel.name);

  return (
    <Paper elevation={6} sx={{
      p: 4,
      borderRadius: 4,
      background: `linear-gradient(135deg, ${alpha(currentLevel.color, 0.08)}, ${alpha('#f8faf8', 0.95)})`,
      border: `3px solid ${levelStyle.border}`,
      backdropFilter: 'blur(10px)',
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: `linear-gradient(90deg, ${currentLevel.color}, ${alpha(currentLevel.color, 0.7)})`,
      }
    }}>
      <Typography variant="h4" fontWeight={800} sx={{
        mb: 4,
        color: currentLevel.color,
        background: `linear-gradient(135deg, ${currentLevel.color}, ${alpha(currentLevel.color, 0.7)})`,
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        textAlign: 'center'
      }}>
        🌱 Eco Statistics
      </Typography>

      {/* Current Level Badge */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Badge
          badgeContent={currentLevel.icon}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '1.8rem',
              minWidth: '3.5rem',
              height: '3.5rem',
              background: levelStyle.bg,
              color: levelStyle.color,
              border: `3px solid ${levelStyle.border}`,
              borderRadius: '50%',
              boxShadow: `0 8px 32px ${alpha(currentLevel.color, 0.4)}`
            }
          }}
        >
          <Avatar sx={{
            width: 140,
            height: 140,
            background: `linear-gradient(135deg, ${currentLevel.color}, ${alpha(currentLevel.color, 0.8)})`,
            fontSize: '4rem',
            boxShadow: `0 16px 48px ${alpha(currentLevel.color, 0.4)}`,
            border: `4px solid ${levelStyle.border}`
          }}>
            <EmojiNature sx={{ fontSize: '4rem', color: 'white' }} />
          </Avatar>
        </Badge>
        <Typography variant="h3" sx={{
          mt: 2,
          color: currentLevel.color,
          fontWeight: 800,
          textShadow: `0 2px 8px ${alpha(currentLevel.color, 0.3)}`
        }}>
          {currentLevel.name}
        </Typography>
        <Typography variant="h6" sx={{
          color: '#546e7a',
          fontWeight: 600,
          background: `linear-gradient(135deg, #ffffff, #f5fbf7)`,
          padding: '12px 24px',
          borderRadius: '24px',
          display: 'inline-block',
          mt: 1,
          boxShadow: '0 4px 20px rgba(67, 160, 71, 0.1)',
          border: `2px solid ${alpha(currentLevel.color, 0.2)}`
        }}>
          {user?.rewardsPoints || 0} Eco Points
        </Typography>
      </Box>

      {/* Progress to Next Level */}
      {nextLevel ? (
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" fontWeight={700} sx={{ color: currentLevel.color }}>
              🎯 Progress to {nextLevel.name}
            </Typography>
            <Typography variant="h6" fontWeight={700} sx={{
              color: '#43a047',
              background: alpha('#43a047', 0.1),
              px: 2,
              py: 0.5,
              borderRadius: 2
            }}>
              {nextLevel.minPoints - (user?.rewardsPoints || 0)} points needed
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(progressToNext, 100)}
            sx={{
              height: 20,
              borderRadius: 10,
              backgroundColor: alpha(currentLevel.color, 0.15),
              '& .MuiLinearProgress-bar': {
                background: `linear-gradient(90deg, ${currentLevel.color}, ${alpha(currentLevel.color, 0.8)})`,
                borderRadius: 10,
                boxShadow: `0 4px 20px ${alpha(currentLevel.color, 0.4)}`
              }
            }}
          />
          <Typography variant="body2" sx={{
            mt: 1,
            textAlign: 'center',
            fontWeight: 600,
            color: currentLevel.color
          }}>
            {progressToNext.toFixed(1)}% Complete
          </Typography>
        </Box>
      ) : (
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h5" fontWeight={700} sx={{
            color: '#ffd700',
            background: 'linear-gradient(135deg, #ffd700, #ffb300)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: '0 2px 8px rgba(255, 215, 0, 0.3)'
          }}>
            👑 Maximum Level Achieved!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            You've reached the pinnacle of eco excellence!
          </Typography>
        </Box>
      )}

      {/* Stats Grid */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Box sx={{
            textAlign: 'center',
            p: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #ffffff, #f8fff8)',
            border: '2px solid #e8f5e8',
            height: '100%',
            boxShadow: '0 4px 20px rgba(67, 160, 71, 0.08)'
          }}>
            <Typography variant="h3" fontWeight={800} sx={{ color: '#43a047', mb: 1 }}>
              {totalClassifications}
            </Typography>
            <Typography variant="h6" fontWeight={600} color="text.primary">
              Total Classifications
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Keep sorting to save our planet! 🌍
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{
            textAlign: 'center',
            p: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #ffffff, #fff8f0)',
            border: '2px solid #ffecb3',
            height: '100%',
            boxShadow: '0 4px 20px rgba(255, 196, 0, 0.08)'
          }}>
            <Typography variant="h3" fontWeight={800} sx={{ color: '#ff8f00', mb: 1 }}>
              {accuracyRate}%
            </Typography>
            <Typography variant="h6" fontWeight={600} color="text.primary">
              Accuracy Rate
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Precision in classification! 🎯
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{
            textAlign: 'center',
            p: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #ffffff, #fff3e0)',
            border: '2px solid #ffcc02',
            height: '100%',
            boxShadow: '0 4px 20px rgba(255, 87, 34, 0.08)'
          }}>
            <Typography variant="h3" fontWeight={800} sx={{ color: '#ff5722', mb: 1 }}>
              🔥 {daysStreak}
            </Typography>
            <Typography variant="h6" fontWeight={600} color="text.primary">
              Day{daysStreak !== 1 ? 's' : ''} Streak
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Consistency is the key to success!
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{
            textAlign: 'center',
            p: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #ffffff, #f1f8e9)',
            border: '2px solid #c8e6c9',
            boxShadow: '0 4px 20px rgba(67, 160, 71, 0.08)'
          }}>
            <Typography variant="h5" fontWeight={700} sx={{ color: '#2e7d32', mb: 1 }}>
              🏅 Top Category
            </Typography>
            <Typography variant="h4" fontWeight={800} sx={{
              color: currentLevel.color,
              background: alpha(currentLevel.color, 0.1),
              px: 3,
              py: 1,
              borderRadius: 3,
              display: 'inline-block',
              border: `2px solid ${alpha(currentLevel.color, 0.2)}`
            }}>
              {topCategory}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Your specialty in waste classification
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default EcoStats;
